import React, {useEffect, useState} from "react";
import * as Yup from "yup";
import { AiFillEyeInvisible, AiFillEye } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { loginService } from "../../../../services/authService/loginService";
import { toast } from "react-toastify";
import { loginSuccess, setAccessToken, setUserData } from "../../../../redux/authSlice";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { profileSettingService } from "../../../../services/profileSettingService/profileSettingService";
import { useParams } from "react-router";
import { useLocation } from "react-router-dom";

const RESEND_COOLDOWN_SEC = 30;

const LoginContentForm = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userType, setUserType] = useState("");
    const [isSent, setIsSent] = useState(false);
    const [lastEmail, setLastEmail] = useState("");
    const [resendLeft, setResendLeft] = useState(0);

    const initialValues = {
        device_type: "",
        email: "",
        password: "",
        device_token: "",
    };

    const passwordValidationSchema = Yup.object().shape({
        password: Yup.string()
            .test(
                "noLeadingSpaces",
                "Invalid Input, Please avoid leading spaces at the beginning of the field",
                (value) => !value || value.trimStart() === value
            )
            .required("Password is required"),
    });

    const generalValidationSchema = Yup.object().shape({
        email: Yup.string()
            .transform((value) => value.trimEnd())
            .test(
                "noLeadingSpaces",
                "Invalid Input, Please avoid leading spaces at the beginning of the field",
                (value) => !value || value.trimStart() === value
            )
            .email("Invalid email format")
            .required("Email is required"),
    });

    const formik = useFormik({
        initialValues,
        validationSchema: () =>
            userType === 3 || userType === 4
                ? generalValidationSchema.concat(passwordValidationSchema)
                : generalValidationSchema,
        onSubmit: async (values) => {
            try {
                setIsSubmitting(true);
                console.log('onSubmit loginLinkRequest')
                await loginService.loginLinkRequest(values);
                setLastEmail(values.email);
                setIsSent(true);
                setResendLeft(RESEND_COOLDOWN_SEC);
                toast.success("Check your inbox for a sign-in link.");
            } catch (ex) {
                toast.error(ex?.response?.data?.message || "Something went wrong");
                handleNetworkError(ex);
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    const handleNetworkError = (error) => {
        console.error("Network error:", error);

        if (!error.response) {
            // The server is not responding or the network is down
            toast.error("Network error. Check your internet connection.");
        } else if (error.code === "ECONNABORTED") {
            // Request timeout
            toast.error("The server is not responding. Try again later.");
        } else if (error.response.status === 403) {
            // Request blocked (eg Firewall)
            toast.error("Access Denied. Your IP may be blocked.");
        } else if (error.response.status >= 500) {
            // Server side problems
            toast.error("Server error. Try again later.");
        } else {
            // Other errors
            toast.error(error.response.data?.message || "An error occurred. Please try again or contact support.");
        }
    };

    useEffect(() => {
        if (!isSent || resendLeft <= 0) return;
        const id = setInterval(() => setResendLeft((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(id);
    }, [isSent, resendLeft]);

    const handleResend = async () => {
        if (resendLeft > 0 || !lastEmail) return;
        try {
            setIsSubmitting(true);
            await loginService.loginLinkRequest({ email: lastEmail });
            setResendLeft(RESEND_COOLDOWN_SEC);
            toast.success("Link resent. Please check your inbox.");
        } catch (ex) {
            toast.error(ex?.response?.data?.message || "Unable to resend");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChangeEmail = () => {
        setIsSent(false);
        setResendLeft(0);
    };

    const handlePassword = () => setShowPassword((v) => !v);

    return (
        <div className="login-form-content">
            <div className="login-form-title mb-4 mb-sm-5 ">
                {isSent ? "Check your email" : "Login"}
            </div>

            {!isSent ? (
                <form onSubmit={formik.handleSubmit} className="login-form-field">
                    <div className="form-group mb-4">
                        {userType !== 3 && userType !== 4 ? (
                            <input
                                type="text"
                                id="email"
                                name="email"
                                className="form-control login-form-group"
                                placeholder="Email address"
                                value={formik.values.email}
                                onChange={(e) => {
                                    formik.handleChange(e);
                                    setIsSubmitting(false);
                                }}
                                onBlur={formik.handleBlur}
                            />
                        ) : (
                            <div className="form-control login-form-group">
                                {formik.values.email}
                            </div>
                        )}
                        {formik.touched.email && formik.errors.email ? (
                            <div className="text-danger" style={{ fontSize: "15px" }}>
                                {formik.errors.email}
                            </div>
                        ) : null}
                    </div>

                    {(userType === 3 || userType === 4) && (
                        <div className="form-group mb-4 position-relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                name="password"
                                placeholder="Password"
                                className="form-control login-form-group"
                                value={formik.values.password}
                                onChange={(e) => {
                                    formik.handleChange(e);
                                    setIsSubmitting(false);
                                }}
                                onBlur={formik.handleBlur}
                            />
                            <button
                                className="btn position-absolute top-0 end-0"
                                type="button"
                                id="show-password"
                                onClick={handlePassword}
                                style={{ height: "48px" }}
                            >
                                {showPassword ? (
                                    <AiFillEye className="header-icon" />
                                ) : (
                                    <AiFillEyeInvisible className="header-icon" />
                                )}
                            </button>
                            {formik.touched.password && formik.errors.password ? (
                                <div className="text-danger" style={{ fontSize: "15px" }}>
                                    {formik.errors.password}
                                </div>
                            ) : null}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="btn login-button"
                        disabled={!formik.dirty || isSubmitting}
                    >
                        {isSubmitting ? "Sending..." : "Login"}
                    </button>
                </form>
            ) : (
                <div className="login-form-field">
                    <div className="alert alert-info mb-3">
                        We’ve sent a one-time sign-in link to <strong>{lastEmail}</strong>.
                        Please check your inbox.
                    </div>

                    <div className="small text-muted mb-4">
                        The link expires in 15 minutes and can be used only once.
                    </div>

                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={handleResend}
                            disabled={isSubmitting || resendLeft > 0}
                        >
                            {resendLeft > 0 ? `Resend in ${resendLeft}s` : "Resend link"}
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleChangeEmail}
                        >
                            Change email
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoginContentForm;